'use client';
import { useState, useEffect, useRef } from 'react';

interface Stat {
  id: string
  value: number
  suffix: string
  label: string
}

export default function StatsSection() {
  const [counters, setCounters] = useState<Record<string, number>>({})
  const sectionRef = useRef<HTMLDivElement>(null)
  const [hasAnimated, setHasAnimated] = useState(false)

  const stats: Stat[] = [
    { id: 'stat_years', value: 10, suffix: '+', label: 'Years Experience' },
    { id: 'stat_team', value: 50, suffix: '+', label: 'Team Members' },
    { id: 'stat_projects', value: 500, suffix: '+', label: 'Projects Completed' },
    { id: 'stat_industries', value: 50, suffix: '+', label: 'Industries Served' },
  ]

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated) {
            setHasAnimated(true)
            stats.forEach((stat) => {
              animateCounter(stat.id, stat.value)
            })
          }
        })
      },
      { threshold: 0.5 }
    )

    if (sectionRef.current) {
      observer.observe(sectionRef.current)
    }

    return () => observer.disconnect()
  }, [hasAnimated])

  const animateCounter = (id: string, target: number) => {
    const duration = 2000
    const steps = 60
    const increment = target / steps
    let current = 0

    const timer = setInterval(() => {
      current += increment
      if (current >= target) {
        current = target
        clearInterval(timer)
      }
      setCounters((prev) => ({ ...prev, [id]: Math.floor(current) }))
    }, duration / steps)
  }

  return (
    <section ref={sectionRef} className="py-20 md:py-32 bg-background">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map((stat, idx) => (
            <div
              key={stat.id}
              className="text-center reveal"
              style={{ transitionDelay: `${idx * 100}ms` }}
            >
              <div className="text-5xl md:text-6xl font-bold text-white mb-2">
                {counters[stat.id] || 0}
                {stat.suffix}
              </div>
              <div className="text-sm text-zinc-500 uppercase tracking-wider">
                {stat.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}